import React, { useEffect, useState } from 'react';
import MockDataService from '../../../services/MockDataService';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Input } from '../../../components/ui';
import { 
  Search, BookOpen, Star, Eye, Bookmark, Clock, Filter, 
  ChevronRight, ChevronDown, Download, ThumbsUp, ThumbsDown,
  MessageCircle, User, Calendar, Tag, ArrowRight, RefreshCw
} from 'lucide-react';

const KnowledgeBaseAccess = () => {
  const [knowledgeBaseData, setKnowledgeBaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('browse');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  useEffect(() => {
    const fetchKnowledgeBaseData = async () => {
      try {
        setLoading(true);
        const response = await MockDataService.getKnowledgeBaseAccess();
        if (response.success) {
          setKnowledgeBaseData(response.data);
        } else {
          setError(response.message);
        }
      } catch (err) {
        setError('Failed to fetch knowledge base data.');
        console.error('Error fetching knowledge base data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchKnowledgeBaseData();
  }, []);

  const handleSearch = (query) => {
    setSearchQuery(query);
    // In a real app, this would trigger a search API call
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedArticle(null);
  };

  const handleArticleSelect = (article) => {
    setSelectedArticle(article);
    setActiveTab('article');
  };

  const handleBookmarkToggle = (articleId) => {
    // In a real app, this would toggle bookmark status
    alert(`Bookmark toggled for article ${articleId}`);
  };

  const handleRatingSubmit = (articleId, rating, helpful) => {
    // In a real app, this would submit the rating
    alert(`Rating submitted for article ${articleId}: ${rating} stars, helpful: ${helpful}`);
  };

  const handleCommentSubmit = (articleId, comment) => {
    // In a real app, this would submit the comment
    alert(`Comment submitted for article ${articleId}: ${comment}`);
  };

  const toggleCategoryExpansion = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const getFilteredArticles = () => {
    if (!knowledgeBaseData) return [];
    
    let articles = knowledgeBaseData.knowledgeBaseAccess.articles;
    
    // Filter by category
    if (selectedCategory !== 'all') {
      articles = articles.filter(article => 
        article.category.toLowerCase() === selectedCategory.toLowerCase()
      );
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      articles = articles.filter(article =>
        article.title.toLowerCase().includes(query) ||
        article.content.toLowerCase().includes(query) ||
        article.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }
    
    return articles;
  };

  const getPopularArticles = () => {
    if (!knowledgeBaseData) return [];
    const filtered = getFilteredArticles();
    return filtered
      .sort((a, b) => b.views - a.views)
      .slice(0, 5);
  };

  const getRecentArticles = () => {
    if (!knowledgeBaseData) return [];
    const filtered = getFilteredArticles();
    return filtered
      .sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated))
      .slice(0, 5);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatViews = (views) => {
    if (views >= 1000) {
      return `${(views / 1000).toFixed(1)}k`;
    }
    return views.toString();
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />);
    }
    
    if (hasHalfStar) {
      stars.push(<Star key="half" className="h-4 w-4 text-yellow-400 fill-current opacity-50" />);
    }
    
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`empty-${i}`} className="h-4 w-4 text-gray-300" />);
    }
    
    return stars;
  };

  if (loading) {
    return <p className="p-6">Loading knowledge base...</p>;
  }

  if (error) {
    return <p className="p-6 text-red-500">Error: {error}</p>;
  }

  if (!knowledgeBaseData) {
    return <p className="p-6">No knowledge base data found.</p>;
  }

  // Safely destructure with defaults
  const { 
    knowledgeBaseAccess = {}, 
    knowledgeBaseStats = {} 
  } = knowledgeBaseData;
  
  const { 
    search = { query: '', filters: {}, results: [], suggestions: [], recentSearches: [] },
    categories = [],
    articles = [],
    bookmarks = [],
    history = [],
    comments = []
  } = knowledgeBaseAccess;

  const tabs = [
    { id: 'browse', label: 'Browse', icon: BookOpen },
    { id: 'search', label: 'Search', icon: Search },
    { id: 'bookmarks', label: 'Bookmarks', icon: Bookmark },
    { id: 'history', label: 'History', icon: Clock }
  ];

  const filteredArticles = getFilteredArticles();
  const popularArticles = getPopularArticles();
  const recentArticles = getRecentArticles();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark">
            Knowledge Base
          </h1>
          <p className="text-text-secondary-light dark:text-text-secondary-dark">
            Find solutions to common problems and learn how to use our systems
          </p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="flex items-center space-x-2">
            <Filter size={18} />
            <span>Filters</span>
          </Button>
          <Button variant="outline" className="flex items-center space-x-2">
            <RefreshCw size={18} />
            <span>Refresh</span>
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Total Articles</p>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                  {knowledgeBaseStats.totalArticles}
                </p>
              </div>
              <BookOpen className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Total Views</p>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                  {formatViews(knowledgeBaseStats.totalViews)}
                </p>
              </div>
              <Eye className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Avg Rating</p>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                  {knowledgeBaseStats.averageRating}/5
                </p>
              </div>
              <Star className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Success Rate</p>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                  {knowledgeBaseStats.searchSuccessRate}%
                </p>
              </div>
              <Search className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-text-secondary-light dark:text-text-secondary-dark" />
              <Input
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline">Advanced Search</Button>
          </div>
          {search.suggestions.length > 0 && (
            <div className="mt-4">
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2">Popular searches:</p>
              <div className="flex flex-wrap gap-2">
                {search.suggestions.slice(0, 5).map((suggestion, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => handleSearch(suggestion)}
                    className="text-xs"
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tab Navigation */}
      <div className="border-b border-border-light dark:border-border-dark">
        <nav className="flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark'
                }`}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'browse' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Categories */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>Categories</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {categories.map((category) => (
                  <div key={category.id} className="space-y-1">
                    <button
                      onClick={() => {
                        handleCategorySelect(category.id);
                        toggleCategoryExpansion(category.id);
                      }}
                      className={`w-full flex items-center justify-between p-2 rounded-md hover:bg-surface-light dark:hover:bg-surface-dark ${
                        selectedCategory === category.id ? 'bg-primary-50 dark:bg-primary-900' : ''
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <BookOpen className="h-4 w-4" />
                        <span className="text-sm font-medium">{category.name}</span>
                        <Badge variant="secondary">{category.articleCount}</Badge>
                      </div>
                      {category.subcategories.length > 0 && (
                        expandedCategories.has(category.id) ? 
                          <ChevronDown className="h-4 w-4" /> : 
                          <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                    {expandedCategories.has(category.id) && category.subcategories.length > 0 && (
                      <div className="ml-6 space-y-1">
                        {category.subcategories.map((subcategory) => (
                          <button
                            key={subcategory.id}
                            onClick={() => handleCategorySelect(subcategory.id)}
                            className={`w-full flex items-center justify-between p-2 rounded-md hover:bg-surface-light dark:hover:bg-surface-dark text-sm ${
                              selectedCategory === subcategory.id ? 'bg-primary-50 dark:bg-primary-900' : ''
                            }`}
                          >
                            <span>{subcategory.name}</span>
                            <Badge variant="outline">{subcategory.articleCount}</Badge>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Articles */}
          <div className="lg:col-span-2 space-y-6">
            {/* Popular Articles */}
            <Card>
              <CardHeader>
                <CardTitle>Popular Articles</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {popularArticles.map((article) => (
                    <div key={article.id} className="flex items-center justify-between p-4 border border-border-light dark:border-border-dark rounded-lg hover:bg-surface-light dark:hover:bg-surface-dark">
                      <div className="flex-1">
                        <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark mb-1">
                          {article.title}
                        </h4>
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2">
                          {article.summary}
                        </p>
                        <div className="flex items-center space-x-4 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                          <div className="flex items-center space-x-1">
                            <Eye className="h-3 w-3" />
                            <span>{formatViews(article.views)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            {renderStars(article.rating)}
                            <span>({article.rating})</span>
                          </div>
                          <Badge variant="outline">{article.category}</Badge>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleBookmarkToggle(article.id)}
                        >
                          <Bookmark className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleArticleSelect(article)}
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent Articles */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Articles</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {recentArticles.map((article) => (
                    <div key={article.id} className="flex items-center justify-between p-4 border border-border-light dark:border-border-dark rounded-lg hover:bg-surface-light dark:hover:bg-surface-dark">
                      <div className="flex-1">
                        <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark mb-1">
                          {article.title}
                        </h4>
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2">
                          {article.summary}
                        </p>
                        <div className="flex items-center space-x-4 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-3 w-3" />
                            <span>{formatDate(article.lastUpdated)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            {renderStars(article.rating)}
                            <span>({article.rating})</span>
                          </div>
                          <Badge variant="outline">{article.category}</Badge>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleBookmarkToggle(article.id)}
                        >
                          <Bookmark className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleArticleSelect(article)}
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'search' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Search Results</CardTitle>
            </CardHeader>
            <CardContent>
              {filteredArticles.length > 0 ? (
                <div className="space-y-4">
                  {filteredArticles.map((article) => (
                    <div key={article.id} className="flex items-center justify-between p-4 border border-border-light dark:border-border-dark rounded-lg hover:bg-surface-light dark:hover:bg-surface-dark">
                      <div className="flex-1">
                        <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark mb-1">
                          {article.title}
                        </h4>
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2">
                          {article.summary}
                        </p>
                        <div className="flex items-center space-x-4 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                          <div className="flex items-center space-x-1">
                            <Eye className="h-3 w-3" />
                            <span>{formatViews(article.views)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            {renderStars(article.rating)}
                            <span>({article.rating})</span>
                          </div>
                          <Badge variant="outline">{article.category}</Badge>
                          <div className="flex items-center space-x-1">
                            {article.tags.slice(0, 3).map((tag, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                <Tag className="h-2 w-2 mr-1" />
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleBookmarkToggle(article.id)}
                        >
                          <Bookmark className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleArticleSelect(article)}
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Search className="h-12 w-12 text-text-secondary-light dark:text-text-secondary-dark mx-auto mb-4" />
                  <p className="text-text-secondary-light dark:text-text-secondary-dark">
                    {searchQuery ? 'No articles found matching your search.' : 'Enter a search term to find articles.'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'bookmarks' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Bookmarked Articles</CardTitle>
            </CardHeader>
            <CardContent>
              {bookmarks.length > 0 ? (
                <div className="space-y-4">
                  {bookmarks.map((bookmark) => {
                    const article = articles.find(a => a.id === bookmark.articleId);
                    if (!article) return null;
                    
                    return (
                      <div key={bookmark.id} className="flex items-center justify-between p-4 border border-border-light dark:border-border-dark rounded-lg hover:bg-surface-light dark:hover:bg-surface-dark">
                        <div className="flex-1">
                          <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark mb-1">
                            {article.title}
                          </h4>
                          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2">
                            {article.summary}
                          </p>
                          {bookmark.notes && (
                            <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark italic">
                              Note: {bookmark.notes}
                            </p>
                          )}
                          <div className="flex items-center space-x-4 text-xs text-text-secondary-light dark:text-text-secondary-dark mt-2">
                            <div className="flex items-center space-x-1">
                              <Calendar className="h-3 w-3" />
                              <span>Bookmarked {formatDate(bookmark.createdAt)}</span>
                            </div>
                            <Badge variant="outline">{article.category}</Badge>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleBookmarkToggle(article.id)}
                          >
                            <Bookmark className="h-4 w-4 fill-current" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleArticleSelect(article)}
                          >
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Bookmark className="h-12 w-12 text-text-secondary-light dark:text-text-secondary-dark mx-auto mb-4" />
                  <p className="text-text-secondary-light dark:text-text-secondary-dark">
                    No bookmarked articles yet. Bookmark articles you find helpful!
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Recently Viewed Articles</CardTitle>
            </CardHeader>
            <CardContent>
              {history.length > 0 ? (
                <div className="space-y-4">
                  {history.map((historyItem) => {
                    const article = articles.find(a => a.id === historyItem.articleId);
                    if (!article) return null;
                    
                    return (
                      <div key={historyItem.id} className="flex items-center justify-between p-4 border border-border-light dark:border-border-dark rounded-lg hover:bg-surface-light dark:hover:bg-surface-dark">
                        <div className="flex-1">
                          <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark mb-1">
                            {article.title}
                          </h4>
                          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2">
                            {article.summary}
                          </p>
                          <div className="flex items-center space-x-4 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                            <div className="flex items-center space-x-1">
                              <Clock className="h-3 w-3" />
                              <span>Viewed {formatDate(historyItem.viewedAt)}</span>
                            </div>
                            <Badge variant="outline">{article.category}</Badge>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleBookmarkToggle(article.id)}
                          >
                            <Bookmark className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleArticleSelect(article)}
                          >
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Clock className="h-12 w-12 text-text-secondary-light dark:text-text-secondary-dark mx-auto mb-4" />
                  <p className="text-text-secondary-light dark:text-text-secondary-dark">
                    No recently viewed articles. Start browsing to see your history here!
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Article Detail Modal */}
      {selectedArticle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-light dark:bg-surface-dark rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-border-light dark:border-border-dark">
              <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
                {selectedArticle.title}
              </h3>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBookmarkToggle(selectedArticle.id)}
                >
                  <Bookmark className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedArticle(null)}
                >
                  ×
                </Button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-6">
                {/* Article Meta */}
                <div className="flex items-center space-x-4 text-sm text-text-secondary-light dark:text-text-secondary-dark">
                  <div className="flex items-center space-x-1">
                    <User className="h-4 w-4" />
                    <span>{selectedArticle.author}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Calendar className="h-4 w-4" />
                    <span>Updated {formatDate(selectedArticle.lastUpdated)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Eye className="h-4 w-4" />
                    <span>{formatViews(selectedArticle.views)} views</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    {renderStars(selectedArticle.rating)}
                    <span>({selectedArticle.rating})</span>
                  </div>
                  <Badge variant="outline">{selectedArticle.category}</Badge>
                </div>

                {/* Article Content */}
                <div className="prose dark:prose-invert max-w-none">
                  <div className="whitespace-pre-line text-text-primary-light dark:text-text-primary-dark">
                    {selectedArticle.content}
                  </div>
                </div>

                {/* Tags */}
                {selectedArticle.tags.length > 0 && (
                  <div>
                    <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark mb-2">Tags</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedArticle.tags.map((tag, index) => (
                        <Badge key={index} variant="secondary">
                          <Tag className="h-3 w-3 mr-1" />
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Attachments */}
                {selectedArticle.attachments.length > 0 && (
                  <div>
                    <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark mb-2">Attachments</h4>
                    <div className="space-y-2">
                      {selectedArticle.attachments.map((attachment) => (
                        <div key={attachment.id} className="flex items-center justify-between p-3 border border-border-light dark:border-border-dark rounded-lg">
                          <div className="flex items-center space-x-3">
                            <Download className="h-4 w-4 text-text-secondary-light dark:text-text-secondary-dark" />
                            <div>
                              <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                                {attachment.name}
                              </p>
                              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                                {attachment.type.toUpperCase()} • {attachment.size}
                              </p>
                            </div>
                          </div>
                          <Button variant="outline" size="sm">
                            Download
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rating Section */}
                <div className="border-t border-border-light dark:border-border-dark pt-6">
                  <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark mb-4">Was this article helpful?</h4>
                  <div className="flex items-center space-x-4">
                    <Button
                      variant="outline"
                      onClick={() => handleRatingSubmit(selectedArticle.id, 5, true)}
                      className="flex items-center space-x-2"
                    >
                      <ThumbsUp className="h-4 w-4" />
                      <span>Yes</span>
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleRatingSubmit(selectedArticle.id, 1, false)}
                      className="flex items-center space-x-2"
                    >
                      <ThumbsDown className="h-4 w-4" />
                      <span>No</span>
                    </Button>
                  </div>
                  <div className="mt-2 text-sm text-text-secondary-light dark:text-text-secondary-dark">
                    {selectedArticle.helpfulVotes} found this helpful, {selectedArticle.notHelpfulVotes} did not
                  </div>
                </div>

                {/* Comments Section */}
                <div className="border-t border-border-light dark:border-border-dark pt-6">
                  <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark mb-4">Comments</h4>
                  <div className="space-y-4">
                    {comments.filter(comment => comment.articleId === selectedArticle.id).map((comment) => (
                      <div key={comment.id} className="p-4 border border-border-light dark:border-border-dark rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <User className="h-4 w-4 text-text-secondary-light dark:text-text-secondary-dark" />
                            <span className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                              User {comment.userId}
                            </span>
                            <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                              {formatDate(comment.createdAt)}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button variant="outline" size="sm">
                              <ThumbsUp className="h-3 w-3" />
                            </Button>
                            <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                              {comment.likes}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-text-primary-light dark:text-text-primary-dark">
                          {comment.content}
                        </p>
                      </div>
                    ))}
                    <div className="p-4 border border-border-light dark:border-border-dark rounded-lg">
                      <textarea
                        placeholder="Add a comment..."
                        className="w-full p-3 border border-border-light dark:border-border-dark rounded-md bg-surface-light dark:bg-surface-dark text-text-primary-light dark:text-text-primary-dark resize-none"
                        rows={3}
                      />
                      <div className="flex justify-end mt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCommentSubmit(selectedArticle.id, "New comment")}
                        >
                          <MessageCircle className="h-4 w-4 mr-2" />
                          Post Comment
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBaseAccess;
